import * as React from 'react';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Popover } from 'antd';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { ZoomAuditAction } from 'types';
import { ZoomAuditEntryFieldsFragment } from 'core/graphql';
import { useZoomAuditLog } from 'core/hooks';
import { todayInWarsaw, toApiDate, formatWarsaw } from 'core/utils/zoomTime';
import { ZoomTable, ZoomColumn, ZoomDateRangeFilter } from 'molecules';

type Row = ZoomAuditEntryFieldsFragment;

const prettyJson = (raw?: string | null): string => {
  if (!raw) {
    return '';
  }
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

export const Audit = memo(() => {
  const { t } = useTranslation();

  const [range, setRange] = useState<[DateTime, DateTime]>(() => [
    todayInWarsaw().minus({ days: 6 }),
    todayInWarsaw(),
  ]);
  const [action, setAction] = useState<string | undefined>();

  const filters = useMemo(
    () => ({
      action,
      dateFrom: toApiDate(range[0]),
      dateTo: toApiDate(range[1]),
    }),
    [action, range],
  );

  const { data, loading, error, hasNextPage, operations } = useZoomAuditLog(
    filters,
  );

  const actionOptions = useMemo(
    () =>
      Object.values(ZoomAuditAction).map((value) => ({
        value,
        label: `${t(`pages.zoom.audit.action.${value}`)}`,
      })),
    [t],
  );

  const columns: Array<ZoomColumn<Row>> = useMemo(
    () => [
      {
        id: 'createdAt',
        Header: `${t('pages.zoom.audit.columns.timestamp')}`,
        columnWidth: '150px',
        accessor: (row: Row) => formatWarsaw(row.createdAt),
      },
      {
        id: 'user',
        Header: `${t('pages.zoom.audit.columns.user')}`,
        columnWidth: '200px',
        accessor: (row: Row) => row.user?.email ?? '—',
      },
      {
        id: 'action',
        Header: `${t('pages.zoom.audit.columns.action')}`,
        columnWidth: '180px',
        accessor: (row: Row) =>
          t(`pages.zoom.audit.action.${row.action}`, {
            defaultValue: row.action,
          }),
      },
      {
        id: 'target',
        Header: `${t('pages.zoom.audit.columns.target')}`,
        columnWidth: '160px',
        accessor: (row: Row) => `${row.targetKind} #${row.targetId}`,
      },
      {
        id: 'details',
        Header: `${t('pages.zoom.audit.columns.details')}`,
        columnWidth: '120px',
        Cell: ({ row }: { row: { original: Row } }) => {
          const json = prettyJson(row.original.details);
          if (!json) {
            return <span>—</span>;
          }
          return (
            <Popover
              content={<DetailsPre>{json}</DetailsPre>}
              trigger="click"
              placement="left"
            >
              <DetailsLink>{t('pages.zoom.audit.showDetails')}</DetailsLink>
            </Popover>
          );
        },
      },
    ],
    [t],
  );

  return (
    <div>
      <Filters>
        <Select
          allowClear
          style={{ width: 220 }}
          placeholder={t('pages.zoom.audit.filters.action')}
          value={action}
          options={actionOptions}
          onChange={(value) => setAction(value as string | undefined)}
        />
        <ZoomDateRangeFilter
          value={range}
          onChange={(next) => {
            if (next) {
              setRange(next);
            }
          }}
        />
      </Filters>

      <ZoomTable<Row>
        columns={columns}
        data={data}
        loading={loading}
        error={error}
        hasMore={hasNextPage}
        loadMore={operations.fetchMore}
        noDataText={t('pages.zoom.audit.noData')}
      />
    </div>
  );
});

const Filters = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  margin: 16px 0;
`;

const DetailsLink = styled.span`
  color: #2563eb;
  cursor: pointer;
  font-size: 13px;
`;

const DetailsPre = styled.pre`
  max-width: 420px;
  max-height: 320px;
  overflow: auto;
  font-size: 12px;
  margin: 0;
`;
